import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider';
import { useChat } from './Chat/ChatContext';
import { useToast } from './hooks/ToastContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { UnifiedDateRangePicker, UnifiedTimeRangePicker } from './common/UnifiedDateTimePicker';

// Components
import RecentPosts from './RecentPosts';
import MyActivePosts from './MyActivePosts';
import Spinner from './common/Spinner';
import PostDetailsModal from './PostDetailsModal';

import CustomButton9, {
  Button,
  Checkbox1,
  CustomButton1,
  CustomButton10,
  CustomButton11,
  CustomButton2,
  CustomButton3,
  CustomButton4,
  CustomButton5,
  CustomButton6,
  CustomButton7,
  CustomButton8,
  CustomInputBox2,
  ModernCard,
  RefreshButton,
  SearchButton3
} from './Templates';
import { 
  FaCalendarAlt, 
  FaClock, 
  FaTimes, 
  FaFilter,
  FaTh,
  FaThLarge
} from 'react-icons/fa';

// Feature flags
import { FEATURES } from '../config/features';

// Styles
import './Home.css';

/**
 * Home component is the main dashboard page after login
 * Shows search filters, recent posts and navigation options
 */
function Home() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { openChat } = useChat();
  const { user, profileLoading } = useContext(AuthContext);
  const { addToast } = useToast();

  
  // Helper function to filter own posts from RecentPosts
  const filterOwnPosts = (posts) => {
    if (!user) return posts;
    return posts.filter(post => post.user_id !== user.id);
  };
  
  // State for feed refresh functionality
  const [feedRefreshState, setFeedRefreshState] = useState({
    newPostsCount: 0,
    isRefreshing: false,
    lastRefreshTime: new Date()
  });
  const recentPostsRef = useRef();
  
  // State for loading, errors, filters and checkboxes
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    // Load saved preference or default to 'regular'
    return localStorage.getItem('recentPostsViewMode') || 'regular';
  });
  const [filters, setFilters] = useState({
    roles: ['Driver', 'Passenger', 'Package'], // Initialize with all roles
    from: '',
    to: '',
    dateFrom: null,
    dateTo: null,
    timeFrom: null,
    timeTo: null
  });
  const [filterInputs, setFilterInputs] = useState({
    driversChecked: true, // Default to checked
    passengersChecked: true, // Default to checked
    packagesChecked: true, // Default to checked
    fromValue: '',
    toValue: '',
    dateFrom: null,
    dateTo: null,
    timeFrom: null,
    timeTo: null
  });
  
  // UI state for date/time pickers
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  
  // Auto-switch to compact mode on mobile screens (only on initial load)
  useEffect(() => {
    const handleInitialResize = () => {
      // Only auto-switch if user hasn't made a choice yet (first visit)
      const hasUserPreference = localStorage.getItem('recentPostsViewMode');
      if (!hasUserPreference && window.innerWidth < 768) {
        setViewMode('compact');
      }
    };
    
    // Only check on initial load, not on every resize
    handleInitialResize();
  }, []); // Empty dependency array - runs only once on mount

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('recentPostsViewMode', viewMode);
  }, [viewMode]);
  // Initialize to false - let RecentPosts handle initial load
  const [applyFilters, setApplyFilters] = useState(false);
  
  // Modal state for post and event details
  const [postModalState, setPostModalState] = useState({
    isOpen: false,
    id: null,
    type: null
  });
  
  const [eventModalState, setEventModalState] = useState({
    isOpen: false,
    id: null
  });

  // Listen for openPostDetailsModal events
  useEffect(() => {
    const handleOpenPostDetailsModal = (event) => {
      const { id, type } = event.detail;
      if (id && type) {
        console.log('Home component received openPostDetailsModal event:', id, type);
        setPostModalState({
          isOpen: true,
          id,
          type
        });
      }
    };
    
    window.addEventListener('openPostDetailsModal', handleOpenPostDetailsModal);
    
    return () => {
      window.removeEventListener('openPostDetailsModal', handleOpenPostDetailsModal);
    };
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw new Error(error.message);
        }
        
        if (!data.session) {
          navigate('/login');
          return;
        }
        
        console.log('Valid session found');
        
        // Check if user profile is complete
        if (data.session.user) {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('completion_step, plan_type, soft_launch_basic_claimed, basic_claim_date')
            .eq('id', data.session.user.id)
            .single();
            
          // Check if user just got Basic plan (claimed today) and show welcome message
          if (profile?.soft_launch_basic_claimed && profile?.basic_claim_date) {
            const claimDate = new Date(profile.basic_claim_date);
            const today = new Date();
            const isToday = claimDate.toDateString() === today.toDateString();
            
            // Show welcome message if claimed today and they haven't seen it yet
            if (isToday && !sessionStorage.getItem(`basic_welcome_shown_${data.session.user.id}`)) {
              setTimeout(() => {
                addToast({
                  type: 'success',
                  title: '🎉 Welcome to Basic Plan!',
                  description: 'You now have access to package delivery, ad-free experience, and more posts per month!'
                });
                sessionStorage.setItem(`basic_welcome_shown_${data.session.user.id}`, 'true');
              }, 1500);
            }
          }
            
          if (profileError) {
            console.warn('Could not fetch profile completion status:', profileError);
            // If profile doesn't exist, user needs to complete it
            if (profileError.code === 'PGRST116') {
              console.log('No profile found - redirecting to profile completion');
              navigate('/complete-profile', { replace: true });
              return;
            }
          } else if (profile) {
            // Check if profile setup is incomplete
            if (profile.completion_step < 3) {
              console.log('Profile incomplete (step:', profile.completion_step, ') - redirecting to profile completion');
              navigate('/complete-profile', { replace: true });
              return;
            }
          }
        }
      } catch (err) {
        console.error('Session check error:', err);
        setError(err.message);
        
        addToast({
          type: 'error',
          title: t('errors.session_error'),
          description: err.message
        });
        
        setTimeout(() => navigate('/login'), 3000);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSession();
  }, [navigate, addToast, t]);
  
  // Listen for country changes and refresh feed
  useEffect(() => {
    const handleCountryChange = (event) => {
      const { newCountry, message } = event.detail;
      console.log('🌍 Country changed to:', newCountry);
      
      // Show success message
      addToast({
        type: 'success',
        title: '🌍 Country Updated',
        description: message || `Country changed to ${newCountry}. Refreshing feed to show relevant content.`
      });
      
      // Refresh the feed to show posts from the new country
      setTimeout(() => {
        if (recentPostsRef.current) {
          recentPostsRef.current.refresh();
        }
      }, 500);
    };
    
    window.addEventListener('countryChanged', handleCountryChange);
    
    return () => {
      window.removeEventListener('countryChanged', handleCountryChange);
    };
  }, [addToast]);

  /**
   * Handle keyboard events for search
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  /**
   * Handle feed refresh functionality
   */
  const handleFeedRefresh = async () => {
    if (recentPostsRef.current && !feedRefreshState.isRefreshing) {
      await recentPostsRef.current.refresh();
    }
  };

  /**
   * Handle refresh state changes from RecentPosts
   */
  const handleRefreshStateChange = (newState) => {
    setFeedRefreshState(newState);
  };

  /**
   * Handle search filter application
   */
  const handleSearch = () => {
    try {
      console.log('Search button clicked, processing filters...');
      
      const selectedRoles = [];
      if (filterInputs.driversChecked) selectedRoles.push('Driver');
      if (filterInputs.passengersChecked) selectedRoles.push('Passenger');
      if (filterInputs.packagesChecked) selectedRoles.push('Package');
      
      console.log('Selected roles:', selectedRoles, 'Package checked:', filterInputs.packagesChecked);
      
      // Only update filters when search button is clicked
      setFilters({
        roles: selectedRoles,
        from: filterInputs.fromValue.trim(),
        to: filterInputs.toValue.trim(),
        dateFrom: filterInputs.dateFrom,
        dateTo: filterInputs.dateTo,
        timeFrom: filterInputs.timeFrom,
        timeTo: filterInputs.timeTo
      });
      
      // Set flag to apply filters
      console.log('Setting applyFilters to true to trigger data refresh');
      
      // Make sure Package role is handled correctly
      if (filterInputs.packagesChecked) {
        if (!selectedRoles.includes('Package')) {
          console.log('Package checkbox is checked but missing from roles - adding it');
          selectedRoles.push('Package');
        }
        
        // Additional log when only Package is selected to help debug
        if (filterInputs.packagesChecked && !filterInputs.driversChecked && !filterInputs.passengersChecked) {
          console.log('*** ONLY PACKAGE CHECKBOX SELECTED ***');
        }
      }
      
      // Final update to filters with possibly corrected roles array
      setFilters({
        roles: selectedRoles,
        from: filterInputs.fromValue.trim(),
        to: filterInputs.toValue.trim(),
        dateFrom: filterInputs.dateFrom,
        dateTo: filterInputs.dateTo,
        timeFrom: filterInputs.timeFrom,
        timeTo: filterInputs.timeTo
      });
      
      setApplyFilters(true);
      
      // Log the state of the package checkbox and roles array
      console.log('Package checkbox state:', filterInputs.packagesChecked);
      console.log('Final roles array for filtering:', selectedRoles);
      
      // Provide feedback that filters were applied
      addToast({
        type: 'info',
        title: t('filters.applied'),
        description: selectedRoles.length === 0 && !filterInputs.fromValue.trim() && !filterInputs.toValue.trim() 
          ? 'Showing all posts - no filters selected' 
          : t('filters.filters_applied')
      });
    } catch (err) {
      console.error('Search error:', err);
      addToast({
        type: 'error',
        title: t('errors.search_error'),
        description: err.message
      });
    }
  };

  /**
   * Handle creating a new post
   */
  const handleAddPostClick = () => {
    navigate("/PublishPost");
  };

  /**
   * Handle sending a package
   */
  const handleSendPackageClick = () => {
    navigate("/SendPackage");
  };

  /**
   * Navigate to Events page
   */
  const handleEventsButtonClick = () => {
    navigate("/events");
  };

  /**
   * Navigate to Groups page
   */
  const handleGroupsButtonClick = () => {
    navigate('/groups');
  };
  
  /**
   * Handle date/time filter functions
   */
  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
    setShowTimePicker(false); // Close time picker when opening date picker
  };

  const toggleTimePicker = () => {
    setShowTimePicker(!showTimePicker);
    setShowDatePicker(false); // Close date picker when opening time picker
  };

  const handleDateFromChange = (date) => {
    setFilterInputs(prev => ({ ...prev, dateFrom: date }));
  };

  const handleDateToChange = (date) => {
    setFilterInputs(prev => ({ ...prev, dateTo: date }));
  };

  const handleTimeFromChange = (time) => {
    setFilterInputs(prev => ({ ...prev, timeFrom: time }));
  };

  const handleTimeToChange = (time) => {
    setFilterInputs(prev => ({ ...prev, timeTo: time }));
  };

  const clearDateFilters = () => {
    setFilterInputs(prev => ({ 
      ...prev, 
      dateFrom: null, 
      dateTo: null 
    }));
  };

  const clearTimeFilters = () => {
    setFilterInputs(prev => ({ 
      ...prev, 
      timeFrom: null, 
      timeTo: null 
    }));
  };

  const hasDateFilters = filterInputs.dateFrom || filterInputs.dateTo;
  const hasTimeFilters = filterInputs.timeFrom || filterInputs.timeTo;

  /**
   * Format date for display
   */
  const formatDisplayDate = (date) => {
    if (!date) return null;
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  /**
   * Format time for display
   */
  const formatDisplayTime = (time) => {
    if (!time) return null;
    return time.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: false 
    });
  };

  // Handle closing pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.date-time-filter')) {
        setShowDatePicker(false);
        setShowTimePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle post/package view in modal
  const handleViewPost = (id, type) => {
    setPostModalState({
      isOpen: true,
      id,
      type
    });
  };
  
  // Handle event view in modal
  const handleViewEvent = (id) => {
    setEventModalState({
      isOpen: true,
      id
    });
  };
  
  // Close post modal
  const closePostModal = () => {
    setPostModalState({
      isOpen: false,
      id: null,
      type: null
    });
  };
  
  // Close event modal
  const closeEventModal = () => {
    setEventModalState({
      isOpen: false,
      id: null
    });
  };

  // Show loading spinner while checking authentication or profile - but faster
  if (isLoading || profileLoading) {
    return (
      <main className="home-main">
        <div className="home-loading-container">
          <Spinner 
            size="medium" 
            customText={profileLoading ? t('app.loading_profile') : t('app.loading')}
            showText={true}
          />
        </div>
      </main>
    );
  }

  // Show error message if there was a problem
  if (error) {
    return (
      <div className="home-error-container">
        <h2>{t('errors.error_title')}</h2>
        <p>{error}</p>
        <Button
          variant="primary"
          onClick={() => window.location.reload()}
        >
          {t('buttons.retry')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <main className="home-main">
        {/* My Active Posts Component - load after initial render */}
        {user && (
          <React.Suspense fallback={<div style={{ height: '120px' }}></div>}>
            <MyActivePosts />
          </React.Suspense>
        )}
        
        <div className="top-containers">
          <div className="top-box-main">
          <div className="top-box-transparent">
            {/* Top row with Add Post button */}
            <div className="driver-passenger-container">
              <Button
                variant="primary"
                className="home-create-btn"
                onClick={handleAddPostClick}
              >
                {t('buttons.add_post')}
              </Button>
              <Button
                variant="primary"
                className="home-create-btn home-package-btn"
                onClick={handleSendPackageClick}
              >
                {t('buttons.package_post')}
              </Button>
            </div>

            {/* Search section */}
            <div className="search-container">
              <div className="input-container">
                <CustomInputBox2
                  type="text" 
                  placeholder={t('forms.traveling_from')}
                  value={filterInputs.fromValue}
                  onChange={(e) => setFilterInputs({...filterInputs, fromValue: e.target.value})}
                  onKeyDown={handleKeyDown}
                />
                <CustomInputBox2
                  type="text" 
                  placeholder={t('forms.traveling_to')}
                  value={filterInputs.toValue}
                  onChange={(e) => setFilterInputs({...filterInputs, toValue: e.target.value})}
                  onKeyDown={handleKeyDown}
                />
              </div>
              
              {/* Date and Time Filters */}
              <div className="date-time-filters">
               <div className="checkbox-search-container" onKeyDown={handleKeyDown}>
                <Checkbox1 
                  id="drivers-checkbox" 
                  label={t('posts.driver')}
                  checked={filterInputs.driversChecked}
                  onChange={(e) => setFilterInputs({...filterInputs, driversChecked: e.target.checked})}
                  onKeyDown={handleKeyDown}
                />
                <Checkbox1 
                  id="passengers-checkbox" 
                  label={t('posts.passenger')}
                  checked={filterInputs.passengersChecked}
                  onChange={(e) => setFilterInputs({...filterInputs, passengersChecked: e.target.checked})}
                  onKeyDown={handleKeyDown}
                />
                <Checkbox1 
                  id="packages-checkbox" 
                  label={t('posts.package')}
                  checked={filterInputs.packagesChecked}
                  onChange={(e) => setFilterInputs({...filterInputs, packagesChecked: e.target.checked})}
                  onKeyDown={handleKeyDown}
                />
               </div>
            </div>
              <div className="date-time-container">
                <div className="date-time-filter">
                    <div className='filter-icon-frame'>
                      <button 
                        type="button" 
                        className={`filter-icon-button ${hasDateFilters ? 'active' : ''}`}
                        title="Filter by date range"
                        onClick={toggleDatePicker}
                      >
                        <FaCalendarAlt />
                        {hasDateFilters && (
                          <span className="filter-indicator">
                            {filterInputs.dateFrom && filterInputs.dateTo
                              ? `${formatDisplayDate(filterInputs.dateFrom)} - ${formatDisplayDate(filterInputs.dateTo)}`
                              : filterInputs.dateFrom
                              ? `From ${formatDisplayDate(filterInputs.dateFrom)}`
                              : `Until ${formatDisplayDate(filterInputs.dateTo)}`
                            }
                          </span>
                        )}
                        {hasDateFilters && (
                          <FaTimes 
                            className="clear-filter" 
                            onClick={(e) => {
                              e.stopPropagation();
                              clearDateFilters();
                            }}
                          />
                        )}
                      </button>
                    
                    {showDatePicker && (
                      <ModernCard className="filter-dropdown-card" onClick={(e) => e.stopPropagation()}>
                        <div className="filter-dropdown-header">
                          <h4>{t('filters.filter_by_date_range')}</h4>
                        </div>
                          <UnifiedDateRangePicker
                            startDate={filterInputs.dateFrom}
                            endDate={filterInputs.dateTo}
                            onStartDateChange={handleDateFromChange}
                            onEndDateChange={handleDateToChange}
                            theme="filter"
                            showLabels={false}
                          />
                      </ModernCard>
                    )}
                  </div>
                </div>

                <div className="date-time-filter">
                  <div className='filter-icon-frame'>
                  <button 
                    type="button" 
                    className={`filter-icon-button ${hasTimeFilters ? 'active' : ''}`}
                    title="Filter by time range"
                    onClick={toggleTimePicker}
                  >
                    <FaClock />
                    {hasTimeFilters && (
                      <span className="filter-indicator">
                        {filterInputs.timeFrom && filterInputs.timeTo
                          ? `${formatDisplayTime(filterInputs.timeFrom)} - ${formatDisplayTime(filterInputs.timeTo)}`
                          : filterInputs.timeFrom
                          ? `From ${formatDisplayTime(filterInputs.timeFrom)}`
                          : `Until ${formatDisplayTime(filterInputs.timeTo)}`
                        }
                      </span>
                    )}
                    {hasTimeFilters && (
                      <FaTimes 
                        className="clear-filter" 
                        onClick={(e) => {
                          e.stopPropagation();
                          clearTimeFilters();
                        }}
                      />
                    )}
                  </button>
                  
                  {showTimePicker && (
                    <ModernCard className="filter-dropdown-card" onClick={(e) => e.stopPropagation()}>
                      <div className="filter-dropdown-header">
                        <h4>{t('filters.filter_by_time_range')}</h4>
                      </div>
                        <UnifiedTimeRangePicker
                          startTime={filterInputs.timeFrom}
                          endTime={filterInputs.timeTo}
                          onStartTimeChange={handleTimeFromChange}
                          onEndTimeChange={handleTimeToChange}
                          theme="filter"
                          showLabels={false}
                        />
                    </ModernCard>
                  )}
                  </div>
                </div>
                <SearchButton3
                  size="small"
                  onClick={handleSearch}
                  className="home-search-button"
                />
              </div>
            </div>

            {/* Events and Groups buttons - conditionally rendered based on feature flags */}
            {(FEATURES.GROUPS_ENABLED || FEATURES.EVENTS_ENABLED) && (
              <div className="events-advanced-search-container">
                {FEATURES.GROUPS_ENABLED && (
                  <CustomButton6
                    label={t('navigation.groups')}
                    onClick={handleGroupsButtonClick} 
                  />
                )}
                {FEATURES.EVENTS_ENABLED && (
                  <CustomButton6
                    label={t('navigation.events')}
                    onClick={handleEventsButtonClick} 
                  />
                )}
              </div>
            )}
          </div>
          </div>
        </div>
        

      
        {/* Recent posts section - optimized loading */}
        <div className="recent-posts-container">
          <div className="recent-posts-header">
            <div className="header-controls">
              {/* View Mode Toggle */}
              <div className="view-mode-toggle">
                <button 
                  className={`mode-btn ${viewMode === 'regular' ? 'active' : ''}`}
                  onClick={() => setViewMode('regular')}
                  title={t('posts.regular_view_tooltip')}
                  type="button"
                >
                  <FaThLarge />
                </button>
                <button 
                  className={`mode-btn ${viewMode === 'compact' ? 'active' : ''}`}
                  onClick={() => setViewMode('compact')}
                  title={t('posts.compact_view_tooltip')}
                  type="button"
                >
                  <FaTh />
                </button>
              </div>
              
              {/* Existing Refresh Button */}
              <div className="refresh-container">
                <RefreshButton
                  onClick={handleFeedRefresh}
                  disabled={feedRefreshState.isRefreshing}
                  tooltip={feedRefreshState.newPostsCount > 0 ? 
                    t('posts.refresh_with_new_count', { count: feedRefreshState.newPostsCount }) :
                    t('posts.refresh_feed')
                  }
                  size="medium"
                  className={feedRefreshState.newPostsCount > 0 ? 'has-new-posts' : ''}
                />
                {feedRefreshState.newPostsCount > 0 && (
                  <span className="new-posts-badge">
                    {feedRefreshState.newPostsCount}
                  </span>
                )}
              </div>
            </div>
          </div>
          {user && (
            <RecentPosts 
              key={`recent-posts`}
              filters={filters} 
              useModals={true} 
              onViewPost={handleViewPost}
              currentUser={user}
              filterOwnPosts={filterOwnPosts}
              applyFilters={applyFilters}
              setApplyFilters={setApplyFilters}
              refreshRef={recentPostsRef}
              onRefreshStateChange={handleRefreshStateChange}
              viewMode={viewMode}
            />
          )}
        </div>
      </main>
      
      {/* Modals */}
      <PostDetailsModal
        isOpen={postModalState.isOpen}
        onClose={closePostModal}
        id={postModalState.id}
        type={postModalState.type}
      />
    </>
  );
}

export default Home;